import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import CustomButton from '../../../components/common/CustomButton';
import {Label} from 'flowbite-react';
import {useJwt} from '../../../helpers/common';
import {useResourcesService} from '../../../services';
import {setOpenUserMemoryManageModal} from "../../../redux/slices/settings";
import UserProfile from "../UserProfile";
import UserMemoryManageModal from "../../../components/UserMemoryManageModal/UserMemoryManageModal";

const UserPreferencesSection: React.FC = () => {
  const dispatch = useDispatch();
  const jwtData: any = useJwt();
  const org_id = jwtData?.org_id || '';
  const isAdmin = typeof jwtData?.org_role === 'string' && jwtData?.org_role?.toLowerCase() === 'admin';
  const { getUserProfile, updateUserProfile } = useResourcesService();

  const { openUserMemoryManageModal } = useSelector((state: any) => state.settings);

  const [enableUserMemoryStorage, setEnableUserMemoryStorage] = useState<boolean>(false);
  const [referenceUserChatHistory, setReferenceUserChatHistory] = useState<boolean>(false);
  const [initialEnableUserMemoryStorage, setInitialEnableUserMemoryStorage] = useState<boolean>(false);
  const [initialReferenceUserChatHistory, setInitialReferenceUserChatHistory] = useState<boolean>(false);
  const [showManageModal, setShowManageModal] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [updatingSettings, setUpdatingSettings] = useState<boolean>(false);
  const [profile, setProfile] = useState<any>(null);

  const loadProfile = async () => {
    if (org_id) {
      try {
        setIsInitialLoad(true);
        const data: any = await getUserProfile();
        setProfile(data);
        setEnableUserMemoryStorage(data?.enable_user_memory_storage || false);
        setReferenceUserChatHistory(data?.reference_user_chat_history || false);
        setInitialEnableUserMemoryStorage(data?.enable_user_memory_storage || false);
        setInitialReferenceUserChatHistory(data?.reference_user_chat_history || false);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      } finally {
        setIsInitialLoad(false);
      }
    }
  };

  useEffect(() => {
    loadProfile();
  }, [org_id]);

  // If another part of the app (e.g. chat) requested the manage modal, open it once
  useEffect(() => {
    if (openUserMemoryManageModal) {
      setShowManageModal(true);
      // Clear the flag so it doesn't reopen on every navigation
      dispatch(setOpenUserMemoryManageModal(false));
    }
  }, [openUserMemoryManageModal, dispatch]);

  const hasChanges = useMemo(() => {
    return (
      enableUserMemoryStorage !== initialEnableUserMemoryStorage ||
      referenceUserChatHistory !== initialReferenceUserChatHistory
    );
  }, [enableUserMemoryStorage, referenceUserChatHistory, initialEnableUserMemoryStorage, initialReferenceUserChatHistory]);

  const handleSaveChanges = async () => {
    if (!org_id) return;
    setUpdatingSettings(true);
    try {
      const payload = {
        enable_user_memory_storage: enableUserMemoryStorage,
        reference_user_chat_history: referenceUserChatHistory
      };
      await updateUserProfile(payload);
      setInitialEnableUserMemoryStorage(enableUserMemoryStorage);
      setInitialReferenceUserChatHistory(referenceUserChatHistory);
    } catch (error) {
      console.error('Failed to save personalization settings:', error);
    } finally {
      setUpdatingSettings(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-surfacePrimary dark:bg-tertiary-dark w-full">
      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-2">
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="flex items-center justify-start gap-2">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">Personalization</h1>
              </div>
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter'] mt-1">Manage your profile and personalization settings. Cadence Pro may use this information while responding.</p>
            </div>
          </div>
        </div>

        <UserProfile profile={profile} loading={isInitialLoad} onUpdate={loadProfile} />

        <div className="bg-secondary-light dark:bg-secondary-dark rounded-lg  border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Card Header */}
          <div className="dark:bg-secondary-dark bg-gray-100 px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between gap-3">
              <div className="pr-4">
                <div className="flex flex-row items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">User memory</h2>
                  <div className="px-2 py-0.5 border border-blue-600 dark:border-blue-500 rounded-xl text-blue-600 dark:text-blue-400 text-xs font-bold uppercase">
                    Beta
                  </div>
                </div>

                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Control how Cadence Pro uses your saved memory.
                  </p>
                </div>
                <CustomButton onClick={() => setShowManageModal(true)} variant="secondary" className="whitespace-nowrap">
                  Manage
                </CustomButton>
              </div>
            </div>

            {/* Card Content */}
            <div className="px-8 py-6 space-y-6">
              {isInitialLoad ? (
                // Loading Skeleton
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <div className="animate-pulse flex items-center gap-3 w-full">
                        <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="animate-pulse flex items-center gap-3 w-full">
                        <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="animate-pulse">
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mt-2"></div>
                    </div>
                    <div className="animate-pulse">
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mt-2"></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className={`flex items-start gap-3 ${!isAdmin ? 'opacity-60' : ''}`}>
                      <ToggleSwitch
                        id="enable_user_memory_storage"
                        name="enable_user_memory_storage"
                        checked={enableUserMemoryStorage}
                        onChange={(e) => isAdmin && setEnableUserMemoryStorage(e.target.checked)}
                        disabled={!isAdmin}
                        label="Enable user memory storage"
                        className={!isAdmin ? "!cursor-not-allowed" : ""}
                      />
                    </div>

                  <div className={`flex items-start gap-3 ${!isAdmin ? 'opacity-60' : ''}`}>
                    <ToggleSwitch
                      id="reference_user_chat_history"
                      name="reference_user_chat_history"
                      checked={referenceUserChatHistory}
                      onChange={(e) => isAdmin && setReferenceUserChatHistory(e.target.checked)}
                      disabled={!isAdmin}
                      label="Reference chat history"
                      className={!isAdmin ? "!cursor-not-allowed" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}>
                    <Label className="!text-xs text-gray-500 dark:text-gray-400">
                      Let Cadence Pro save and use your user memory while responding.
                    </Label>
                  </div>
                  <div className={!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}>
                    <Label className="!text-xs text-gray-500 dark:text-gray-400">
                      Let Cadence Pro reference recent conversations when responding.
                    </Label>
                  </div>
                </div>
              </>
            )}
            {!isAdmin && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Only administrators can modify these settings.
                </p>
              </div>
            )}
          </div>

            {/* Footer Actions */}
            {isAdmin && (
              <div className="px-8 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-end items-center gap-3">
                  {hasChanges && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                      You have unsaved changes
                    </p>
                  )}
                  <CustomButton
                    variant="primary"
                    onClick={handleSaveChanges}
                    disabled={updatingSettings || !hasChanges}
                    className="!min-w-[120px]"
                  >
                    {updatingSettings ? 'Saving...' : 'Save Changes'}
                  </CustomButton>
                </div>
              </div>
            )}
          </div>


        <UserMemoryManageModal
          isOpen={showManageModal}
          onClose={() => setShowManageModal(false)}
        />

      </div>
    </div>
  );
};

export default UserPreferencesSection;